import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { AnimalModel } from '../../models/animal.model';
import { CARE_OPTIONS, NUTRITION_OPTIONS } from '../../models/body-condition-score.model';
import { AnimalMedicalConditionModel } from '../../models/medical/animal-medical-condition.model';
import { AnimalMedicalRecordModel } from '../../models/medical/animal-medical-record.model';
import {
  getMedicalConditionKindLabelKey,
  getMedicalConditionSeverityLabelKey,
  MedicalConditionSeverity
} from '../../models/medical/medical.enums';
import { gramsToKg } from '../../models/medical/medical.helpers';
import { refId } from '../../models/medical/medical-ref.model';
import { AnimalMedicalRepository } from '../../states/medical/animal-medical.repository';

interface WeightTrend {
  deltaKg: number;
  since: string;
}

/** Lesesicht: Diagnosen (aktive vor abgeheilten) und aktueller Zustand des Tiers. */
@Component({
  selector: 'app-medical-conditions',
  templateUrl: './medical-conditions.component.html',
  styleUrls: ['../medical-shared.scss'],
  standalone: false
})
export class MedicalConditionsComponent implements OnInit, OnDestroy {
  @Input() animal: AnimalModel | null = null;
  @Output() goToRecord = new EventEmitter<number>();

  protected readonly kindLabelKey = getMedicalConditionKindLabelKey;
  protected readonly severityLabelKey = getMedicalConditionSeverityLabelKey;
  protected readonly gramsToKg = gramsToKg;
  protected readonly refId = refId;

  conditions: AnimalMedicalConditionModel[] = [];
  weightTrend: WeightTrend | null = null;

  private readonly _destroy$ = new Subject<void>();

  constructor(private readonly _medicalRepository: AnimalMedicalRepository) {}

  ngOnInit(): void {
    combineLatest([this._medicalRepository.conditions$, this._medicalRepository.records$])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([conditions, records]) => {
        this.conditions = [...conditions].sort((a, b) =>
          Number(!!a.resolvedAt) - Number(!!b.resolvedAt)
          || (b.diagnosedAt ?? '').localeCompare(a.diagnosedAt ?? '')
          || b.id - a.id
        );
        this.weightTrend = this.buildWeightTrend(records);
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  severityClass(severity: MedicalConditionSeverity | null | undefined): 'danger' | 'warn' | 'info' | 'secondary' {
    switch (severity) {
      case MedicalConditionSeverity.HIGH: return 'danger';
      case MedicalConditionSeverity.MEDIUM: return 'warn';
      case MedicalConditionSeverity.LOW: return 'info';
      default: return 'secondary';
    }
  }

  nutritionLabel(value: string | null | undefined): string | null {
    return NUTRITION_OPTIONS.find(option => option.value === value)?.label ?? null;
  }

  careLabels(values: string[] | null | undefined): string[] {
    return (values ?? []).map(value => CARE_OPTIONS.find(option => option.value === value)?.label ?? value);
  }

  sourceLabelKey(source: string | null | undefined): string {
    return `animalMedical.conditions.source.${source ?? 'unknown'}`;
  }

  /** Veränderung gegenüber der vorletzten (nicht korrigierten) Karteikarte mit Gewicht. */
  private buildWeightTrend(records: AnimalMedicalRecordModel[]): WeightTrend | null {
    const weighed = records
      .filter(record => record.weight !== null && !record.corrected)
      .sort((a, b) => b.date.localeCompare(a.date) || b.id - a.id);
    if (weighed.length < 2) return null;
    const current = this.animal?.weight ?? weighed[0].weight!;
    return { deltaKg: (current - weighed[1].weight!) / 1000, since: weighed[1].date };
  }
}
